import { defineStore } from 'pinia';
import axios, { CancelToken } from 'axios';
import {
    defaultFilterValues,
    filtersToApiFilters,
    filtersToUrlParams,
    quickFilters,
} from '../lib/filters';
import { productTypes, sortingOptions } from '../lib/filterOptions';
import {
    convertLocationToUrlQuery, locationPropsToLocationObject,
} from '../../../vue-elements/comparison-lib/url';
import { generateLocationDisplay, getCompareLocation } from '../../../vue-elements/comparison-lib/util';
import { logAnalyticsEvent } from '../../../../assets/js/helpers/bananas.js';

export const useInternetComparisonStore = defineStore('internetComparison', {
    state: () => ({
        // Filters:
        filters: defaultFilterValues(),
        postalCode: null,
        houseNumber: null,
        houseNumberAddition: null,

        // Loading states:
        error: false,
        errorMessage: null,
        errorType: null,

        // Data:
        allProducts: [],
        totalProducts: null,
        compareLocation: null,

        lastChangedFilter: null,
        blackFriday: false,
        fakeProductCount: 3,
        loadingDelayTime: 1000,

        initialLoadDone: false,
        initialLoadTimer: null,

        localFilters: {},

        quickFilters: quickFilters(),
        showMobileFilters: false,
        speedAssistant: false,
        realisticSpeedSet: false,
        firstLoading: true,
        showFrank: false,
        frankProduct: null,
        frankDelay: false,
        forceAddressDisplay: false,
        savedComparison: null,
        sortingOptions,
        productTypes,

        // Config
        apiUrl: 'https://api-internet.keuze.nl/v1/compare/yZv4mJs9qTfWcQz6-554',

        // Internal loading states:
        xhrLoading: false,
        paginating: false,

        axiosCancelToken: null,
    }),
    getters: {
        loading() {
            // Either the API is loading (xhrLoading)
            return this.xhrLoading;
        },
        products() {
            // Return first 10 products
            return this.allProducts.slice(0, this.filters.products);
        },
        comparisonType() {
            return 'internet';
        },
        moreProducts() {
            return this.allProducts.length - this.filters.products;
        },
        buildApiURL() {
            // 5Jm0k9yn1oQqCz6I-268 = Keuze.nl
            let apiUrlWithLocation = 'https://api-internet.keuze.nl/v1/compare/5Jm0k9yn1oQqCz6I-268';

            if (this.postalCode && this.houseNumber) {
                apiUrlWithLocation += `/${this.postalCode}/${this.houseNumber}`;

                if (this.houseNumberAddition) {
                    apiUrlWithLocation += `-${this.houseNumberAddition}`;
                }
            }

            return apiUrlWithLocation;
        },
        hasMoreProducts() {
            return this.totalProducts > this.products.length;
        },
        moreProductCount() {
            return Math.min(10, this.totalProducts - this.products.length);
        },
        buildQuery() {
            return {
                ...convertLocationToUrlQuery(this.compareLocation),
                ...filtersToUrlParams(this.filters),
            };
        },
        maxSpeed() {
            if (
                this.compareLocation != null
                && this.compareLocation.data != null
                && this.compareLocation.data.max_speed != null
            ) {
                return this.compareLocation.data.max_speed;
            }
            return 1000;
        },
        addressReady() {
            if (!this.compareLocation) {
                return false;
            }
            if (this.forceAddressDisplay) {
                return false;
            }
            if (this.compareLocation.lookedUp && !this.compareLocation.data.address_found) {
                return false;
            }
            return true;
        },
    },
    actions: {
        load() {
            this.error = false;
            this.errorMessage = null;

            this.cancelLoad();

            this.axiosCancelToken = CancelToken.source();
            this.xhrLoading = true;
            const apiUrlWithLocation = this.buildApiURL;
            axios
                .get(apiUrlWithLocation, {
                    cancelToken: this.axiosCancelToken.token,
                    params: filtersToApiFilters(this.filters),
                })
                .then((response) => {
                    let rawProducts = response.data.products;

                    rawProducts = rawProducts.map((product) => {
                        product.signup_url = product.signup_url.replace('https://api-internet.whitelabeled.nl', 'https://api-internet.keuze.nl');
                        product.provider_logo = product.provider_logo.replace('https://internet.whitelabeled.nl', 'https://internetvergelijker.keuze.nl');
                        return product;
                    });
                    
                    this.allProducts = rawProducts;
                    this.totalProducts = response.data.total_products;
                    this.xhrLoading = false;
                    this.firstLoading = false;
                    this.axiosCancelToken = null;
                    this.error = false;
                    this.errorType = null;
                    this.errorMessage = null;

                    this.saveProductsToLocalStorage(this.allProducts);

                    if (!this.initialLoadDone) {
                        // Apparently the first load is done. Remember to set realistic speed
                        // when the location is looked up:
                        this.realisticSpeedSet = false;
                        this.initialLoadDone = true;
                    }

                    if (response.data.location !== null) {
                        const rawLocation = response.data.location;

                        this.compareLocation = {
                            source: 'internet',
                            display: generateLocationDisplay(response.data.location),
                            data: response.data.location,
                            lookedUp: true,
                        };

                        // Write location to local storage for postal boxes etc.:
                        localStorage.setItem('compareLocation', JSON.stringify({
                            data: {
                                postCode: rawLocation.postCode,
                                number: rawLocation.number,
                                number_extension: rawLocation.numberExtension,
                            },
                        }));
                    }

                    this.setRealisticSpeedDefault();
                    this.setConnectionTypeDefaults();
                    // Log event:
                    logAnalyticsEvent('compare-internet.load-products', {
                        filters: this.filters,
                    });
                })
                .catch((error) => {
                    if (axios.isCancel(error)) {
                        return;
                    }

                    this.error = true;
                    this.xhrLoading = false;
                    this.paginating = false;
                    this.firstLoading = false;

                    this.errorType = 'unknown';

                    if (error.response) {
                        this.errorMessage = `Op dit moment kunnen we geen producten weergeven. Er trad een fout op bij het laden van de resultaten. (HTTP status${error.response.status})`;
                        this.errorType = `http-${error.response.status}`;
                    } else if (error.request) {
                        this.errorMessage = 'Op dit moment kunnen we geen producten weergeven. Er trad een fout bij het maken van de verbinding.';
                        this.errorType = 'connection';
                    } else {
                        // eslint-disable-next-line no-console
                        console.warn(error);
                        this.errorMessage = `Op dit moment kunnen we geen producten weergeven. Er trad een onbekende fout op: ${error.message}`;
                    }

                    // Log error:
                    logAnalyticsEvent('compare-internet.error', { error_type: this.errorType });
                });
        },
        loadMore() {
            this.paginating = true;

            setTimeout(() => {
                this.filters.products += 10;
                this.paginating = false;
            }, this.loadingDelayTime);
        },
        cancelLoad() {
            if (this.axiosCancelToken != null) {
                this.axiosCancelToken.cancel();
            }
        },
        resetFilters() {
            const defaultFilters = defaultFilterValues();

            const noReset = [];

            Object.keys(defaultFilters)
                .forEach((filterItem) => {
                    if (!noReset.includes(filterItem)) {
                        this.filters[filterItem] = defaultFilters[filterItem];
                    }
                });

            this.realisticSpeedSet = false;
            this.setRealisticSpeedDefault();
        },
        doPostal(postalObj) {
            this.postalCode = postalObj.postCode;
            this.houseNumber = postalObj.nr;

            this.compareLocation = {};
            this.compareLocation.data = {};

            this.compareLocation.data.postCode = postalObj.postCode;
            this.compareLocation.data.number = postalObj.nr;
            this.compareLocation.data.numberExtension = '';
            this.forceAddressDisplay = false;

            this.load();
        },

        setCompareLocation(props) {
            const compareLocationURL = locationPropsToLocationObject(props);

            // Check whether we actually have a compareLocationURL object:
            if (compareLocationURL == null) {
                // We don't, so we never take the local storage object:
                this.compareLocation = null;
                return;
            }
            // Patchwork
            compareLocationURL.data.numberExtension = (compareLocationURL.data.numberExtension ? compareLocationURL.data.numberExtension : '');

            const compareLocationStorage = getCompareLocation();
            // Patchwork
            compareLocationStorage.data.numberExtension = (compareLocationStorage.data.numberExtension ? compareLocationStorage.data.numberExtension : '');

            const compareLocationEqual = (compareLocationURL) && (compareLocationURL.data.number === compareLocationStorage.data.number)
                && (compareLocationURL.data.postCode.toUpperCase() === compareLocationStorage.data.postCode.toUpperCase())
                && (compareLocationURL.data.numberExtension.toUpperCase() === compareLocationStorage.data.numberExtension.toUpperCase());

            if (compareLocationEqual) {
                this.compareLocation = compareLocationStorage;
            } else {
                this.compareLocation = compareLocationURL;
            }
        },
        pickExtension(postCode, number, extension) {
            let houseNumber = number;
            if (extension !== null) {
                houseNumber += `-${extension}`;
            }
            const postalObj = {
                postCode,
                nr: houseNumber,
            };
            this.doPostal(postalObj);
        },
        setRealisticSpeedDefault() {
            if (this.realisticSpeedSet || !this.compareLocation?.lookedUp) {
                return;
            }

            let locationMaxSpeed = this.compareLocation.data.max_fiber ? 100 : this.compareLocation.data.max_dsl;

            if (typeof locationMaxSpeed === 'undefined') {
                locationMaxSpeed = 0;
            }

            if (this.filters.minSpeed === -1 || this.filters.minSpeed === 0) {
                // Set minSpeed filter to sensible defaults:
                if (locationMaxSpeed < 20) {
                    this.filters.minSpeed = 0;
                } else if (locationMaxSpeed < 50) {
                    this.filters.minSpeed = 20;
                } else if (locationMaxSpeed <= 100) {
                    this.filters.minSpeed = 50;
                } else {
                    this.filters.minSpeed = 100;
                }

                this.realisticSpeedSet = true;
            }
        },
        setConnectionTypeDefaults() {
            if (this.compareLocation?.data.max_dsl === null) {
                this.filters.dsl = false;
            }
            if (this.compareLocation?.data.max_cable === null) {
                this.filters.cable = false;
            }
            if (this.compareLocation?.data.max_fiber === null) {
                this.filters.fiber = false;
            }
        },
        setSavedComparison(payload) {
            this.savedComparison = payload;
        },
        saveProductsToLocalStorage(products) {
            if (typeof Storage === 'undefined') {
                return;
            }

            const productDataKey = 'productdata-internet';
            let productData = {};

            // Use existing productData when available:
            if (window.localStorage.getItem(productDataKey) !== null) {
                productData = JSON.parse(window.localStorage.getItem(productDataKey));
            }

            // Set product data for all products:
            products.forEach((product) => {
                productData[product.base_id] = product;
            });

            window.localStorage.setItem(productDataKey, JSON.stringify(productData));
        },
    },
});
